import { PrimaryControlsStripLeading } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import {
  ToolbarPopoverButton,
  ToolbarPopoverChoiceRow,
  type ToolbarPopoverChoiceOption,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import { ToolbarMoreActions } from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import {
  ToolbarPillRow,
  ToolbarViewButton,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useBrowsePrimitivesCompoundsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.primitives.compounds.navigator";
import { useBrowsePrimitivesListPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.primitives.list.navigator";
import { useGetBrowsePrimitivesPageQueryKey } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.primitives.query";
import { useIsFetching } from "@-/com.npmjs.tanstack__react-query";
import { usePathname } from "@-/com.npmjs.expo-router";
import { PrimaryControlsStripToolbar } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";

export type PrimitivesRoute = "list" | "compounds";

/**
 * Derives the active view from the current pathname. The index route (`.../primitives`, no view
 * segment) redirects to List, and the sibling routes end in `/list` or `/compounds`. Matching the
 * pathname (not navigator state) keeps the active pill correct however the route was reached —
 * tapping a pill, the tab, or a deep link.
 */
function primitivesRouteFromPathname(pathname: string): PrimitivesRoute {
  return pathname.endsWith("/compounds") ? "compounds" : "list";
}

/**
 * Persistent left-hand route pills for the Primitives layout, rendered as an overlay sibling of the
 * layout's `Slot` (mirroring the Levels toolbar). Each pill `replace`s between the sibling routes
 * (`list` / `compounds`) via a page navigator, and the active pill is derived from the pathname.
 * The `absolute inset-x-0 top-0 z-10` overlay floats over the list so primitive rows scroll
 * underneath; each rendered route owns its leading controls, including the index route's spinner.
 * PrimaryControlsStripToolbar publishes this row's height so the shared fade starts below the pills.
 */
export function BrowsePrimitivesPageToolbar() {
  const listNavigator = useBrowsePrimitivesListPageNavigator();
  const compoundsNavigator = useBrowsePrimitivesCompoundsPageNavigator();
  const replaceList = listNavigator.replace.mutate;
  const replaceCompounds = compoundsNavigator.replace.mutate;

  const active = primitivesRouteFromPathname(usePathname());

  return (
    <PrimaryControlsStripToolbar
      className="absolute inset-x-0 top-0 z-10"
      // On iOS 26 the bar floats as glass over the primitive lists scrolling beneath it.
      floating
      pointerEvents="box-none"
    >
      <ToolbarPillRow
        accessibilityRole="tablist"
        contentContainerClassName="gap-0.5 p-1"
        hasLeadingActions
      >
        <ToolbarViewButton
          label="Primitives"
          onPress={() => replaceList()}
          selected={active === "list"}
        />
        <ToolbarViewButton
          label="Compounds"
          onPress={() => replaceCompounds()}
          selected={active === "compounds"}
        />
      </ToolbarPillRow>
    </PrimaryControlsStripToolbar>
  );
}

/**
 * Leading controls owned by the rendered Primitives page, not the shared layout.
 *
 * The More/✕ trigger doubles as this view's only progress indicator. Changing the tier scope
 * rotates the page query's key, and the recomputation is slow enough to notice (three full-table
 * reads plus a direct-parent filter over the decomposition edge set). The list deliberately keeps
 * showing the PREVIOUS result while that runs — see
 * {@link useIsBrowsePrimitivesViewRecomputing} — so without the spinner the dropdown would look
 * like it did nothing at all.
 */
export function BrowsePrimitivesPageToolbarActions() {
  const isRecomputingView = useIsBrowsePrimitivesViewRecomputing();

  return (
    <PrimaryControlsStripLeading>
      <ToolbarMoreActions isLoading={isRecomputingView}>
        <PrimitivesTierScopeMenu />
      </ToolbarMoreActions>
    </PrimaryControlsStripLeading>
  );
}

/**
 * True while the primitives list on screen is stale with respect to the controls above it — i.e. a
 * tier-scope change is being recomputed and the rows still describe the OLD scope.
 *
 * Implemented with `useIsFetching` on the CURRENT key (the same house pattern as the `useIsMutating`
 * progress readouts in Learn), NOT with a second `useQuery`. Two reasons, both load-bearing:
 *
 * 1. It OBSERVES the shared cache entry rather than subscribing as another consumer, so mounting
 *    this toolbar can never itself kick off the expensive graph walk. A `useQuery` here would
 *    either duplicate the list's subscription or, with `enabled: false`, report `isFetching: false`
 *    forever and never spin at all.
 * 2. `isPlaceholderData` is unavailable from outside: `usePrefetchedSuspenseQuery` holds the
 *    retained value in its own ref, so only the list's hook can see it.
 *
 * The key rotates the moment the device setting is written, so the count goes to 1 on the NEW key
 * while the list still renders the old rows — which is exactly the window to report. Nothing on the
 * main view changes meanwhile: the retained value keeps the page from re-suspending, so no skeleton
 * is ever mounted.
 *
 * A switch pill needs no readout here: its `replace` commits on the press, and the destination
 * route draws its own skeleton while it loads.
 */
function useIsBrowsePrimitivesViewRecomputing() {
  const getQueryKey = useGetBrowsePrimitivesPageQueryKey();
  return useIsFetching({ queryKey: getQueryKey() }) > 0;
}

/**
 * Tier-scope popover: chooses which characters count as compounds of a primitive. `HSK Only` (the
 * default) restricts compounds to the graded syllabus — HSK-levelled characters for simplified,
 * TBCL-levelled for traditional. `All tiers` widens the set to every General Standard tier-1/2/3
 * character (simplified) or every "common" usage-tier character (traditional). Backed by the
 * `primitivesTierScope` device setting, so both Primitives sub-views share (and persist) it.
 */
function PrimitivesTierScopeMenu() {
  const { hanziScript } = useActorUser();
  const [tierScope, setTierScope] = useDeviceSetting("primitivesTierScope");

  // The default scope is labelled after the syllabus it restricts to (HSK for simplified users,
  // TBCL for traditional users), so the pill reflects the actual grading list in each script.
  const gradedLabel = hanziScript === "traditional" ? "TBCL Only" : "HSK Only";
  const pillLabel = tierScope === "all-tiers" ? "All tiers" : gradedLabel;

  const tierScopeOptions: readonly ToolbarPopoverChoiceOption<typeof tierScope>[] = [
    { label: gradedLabel, testID: "primitives-tier-scope-graded", value: "hsk-only" },
    { label: "All tiers", testID: "primitives-tier-scope-all-tiers", value: "all-tiers" },
  ];

  return (
    <ToolbarPopoverButton
      accessibilityLabel="Filter compounds by tier"
      label={pillLabel}
      name="MdTune"
      // The accent-filled `selected` state reads as "a wider-than-default filter is active".
      selected={tierScope === "all-tiers"}
    >
      <ToolbarPopoverChoiceRow
        label="Count characters from"
        onValueChange={setTierScope}
        options={tierScopeOptions}
        value={tierScope}
      />
    </ToolbarPopoverButton>
  );
}
